<template>
  <div
    class="equipment-unavailable"
    :data-cy="`equipment-unavailable-${issue.equipmentType}`"
  >
    <div class="unavailable-title">
      <icon :icon="['fas', 'triangle-exclamation']" />
      <strong>No {{ equipmentLabel }} candidate available</strong>
    </div>
    <p>{{ issue.message }}</p>
    <dl v-if="issue.details?.length" class="issue-details">
      <template v-for="detail in issue.details" :key="detail.label">
        <dt>{{ detail.label }}</dt>
        <dd>
          {{ formatIssueValue(detail) }}
          <span v-if="detail.relaxed" class="relaxed-badge"
            >Relaxed during retry</span
          >
        </dd>
      </template>
    </dl>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type {
  AIEquipmentIssue,
  AIEquipmentIssueDetail
} from "@/libs/api/aiEquipmentSuggestions";

const props = defineProps<{
  issue: AIEquipmentIssue;
}>();

const equipmentLabel = computed(() =>
  props.issue.equipmentType.replaceAll("_", " ")
);

function formatIssueValue(detail: AIEquipmentIssueDetail): string {
  return `${detail.value}${detail.unit ? ` ${detail.unit}` : ""}`;
}
</script>

<style scoped>
.equipment-unavailable {
  margin: 1rem 0;
  padding: 1rem;
  border: 1px solid var(--orange-300);
  border-radius: 6px;
  background: var(--orange-50);
  color: var(--text-color);
}

.unavailable-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--orange-800);
  text-transform: capitalize;
}

.equipment-unavailable p {
  margin: 0.5rem 0 1rem;
}

.issue-details {
  display: grid;
  grid-template-columns: minmax(10rem, auto) 1fr;
  gap: 0.5rem 1rem;
  margin: 0;
}

.issue-details dt {
  color: var(--text-color-secondary);
  font-weight: 600;
}

.issue-details dd {
  margin: 0;
}

.relaxed-badge {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: var(--orange-100);
  color: var(--orange-800);
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .issue-details {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }

  .issue-details dd {
    margin-bottom: 0.5rem;
  }
}
</style>
